import { useLayoutEffect, useState } from 'react';

/**
 * Which iPhone Duo posture puts the app's navigation in a rail down the screen's outer edge,
 * if any. The layouts follow Figma "iOS 27 on iPhone Duo"; the prototype that specs them is
 * src/pages/AIGenerated/vhc/hooks/useFoldPosture.ts.
 *
 * - `cover`       — folded, 466 × 678. Rail on the right.
 * - `split-left`  — the left half of the unfolded screen, beside another app, 469 × 669.
 *                   Rail on the left.
 * - `split-right` — the right half, the same size. Rail on the right, under the status bar.
 *
 * Unfolded full screen (951 × 669) is wider than the 940px breakpoint and keeps the desktop
 * shell, so it is not a rail posture.
 *
 * The three are the same size; the safe-area insets tell them apart (measured on the Xcode
 * 27.1 simulator): the cover reserves an 82px camera band across the top, the right half an
 * 84px status column down its right edge, the left half nothing.
 *
 * Writes `html[data-fold]` (the posture) and `html[data-fold-side]` (the rail's edge), so
 * styles, portalled ones included, scope to them. No other device ever gets either.
 */
export type FoldPosture = 'cover' | 'split-left' | 'split-right';

const POSTURES: readonly FoldPosture[] = ['cover', 'split-left', 'split-right'];

// A device band, not a breakpoint: no other iPhone is this narrow and this short.
const DUO_HALF =
  '(pointer: coarse) and (min-width: 440px) and (max-width: 500px) and (min-height: 640px) and (max-height: 700px)';

// `?device=fold-<posture>` previews a rail posture in a desktop browser while developing.
const devOverride = (): FoldPosture | undefined => {
  if (!import.meta.env.DEV) return undefined;
  const value = new URLSearchParams(window.location.search).get('device')?.replace(/^fold-/, '');
  return POSTURES.find((posture) => posture === value);
};

const detect = (probe: HTMLElement): FoldPosture | undefined => {
  // The band alone also fits a resized iPad window, an Android multi-window app or a touch
  // laptop's browser. iPadOS WebKit calls itself Macintosh, so only an iPhone passes.
  if (!/iPhone/.test(navigator.userAgent)) return undefined;
  if (!window.matchMedia(DUO_HALF).matches) return undefined;
  const inset = getComputedStyle(probe);
  if (parseFloat(inset.paddingTop) > 0) return 'cover';
  return parseFloat(inset.paddingRight) > 0 ? 'split-right' : 'split-left';
};

export const useFoldPosture = (): FoldPosture | undefined => {
  const [posture, setPosture] = useState<FoldPosture>();

  // Layout effects, so the first paint already has the rail rather than the bottom bar.
  useLayoutEffect(() => {
    // Read once: the query string is gone after the first in-app navigation.
    const override = devOverride();
    // env() only resolves in CSS, so a hidden element padded by the insets reads them.
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;visibility:hidden;' +
      'padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
    document.body.appendChild(probe);

    const update = () => setPosture(override ?? detect(probe));
    // The probe resizes when the insets move, which swapping split sides does without
    // resizing the window.
    const observer = new ResizeObserver(update);
    observer.observe(probe, { box: 'border-box' });
    // The band's change, not `resize`: unfolding moves the insets first, then iOS WebKit can
    // fire its last resize while the viewport still has the half's width, and the rail stayed
    // on the 951 screen (as useIsMobile did on rotation).
    const band = window.matchMedia(DUO_HALF);
    band.addEventListener('change', update);
    update();

    return () => {
      observer.disconnect();
      band.removeEventListener('change', update);
      probe.remove();
    };
  }, []);

  useLayoutEffect(() => {
    if (!posture) return;
    const root = document.documentElement;
    root.dataset.fold = posture;
    root.dataset.foldSide = posture === 'split-left' ? 'left' : 'right';
    return () => {
      delete root.dataset.fold;
      delete root.dataset.foldSide;
    };
  }, [posture]);

  return posture;
};
